<template>
  <div v-if="show" class="modal-overlay" @click.self="$emit('cancel')">
    <div class="delete-modal" :class="{ 'modal-shake': showShake }">
      <!-- Header with danger icon -->
      <div class="modal-header">
        <div class="danger-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" fill="#fee2e2"/>
            <path d="M12 8v4M12 16h.01" stroke="#dc2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3>{{ title || 'Confirmer la suppression' }}</h3>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <p class="main-message">{{ message || 'Êtes-vous sûr de vouloir effectuer cette suppression ?' }}</p>
        
        <!-- Entity details if provided -->
        <div v-if="entityDetails && entityDetails.length > 0" class="entity-details">
          <div v-for="detail in entityDetails" :key="detail.label" class="detail-item">
            <span class="detail-label">{{ detail.label }}:</span>
            <span class="detail-value">{{ detail.value }}</span>
          </div>
        </div>
        
        <!-- Warning message -->
        <div class="warning-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 22h20L12 2z" fill="#fbbf24"/>
            <path d="M12 8v4M12 16h.01" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Cette action est irréversible</span>
        </div>

        <!-- Error message -->
        <div v-if="error" class="error-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" fill="#fecaca"/>
            <path d="M15 9l-6 6M9 9l6 6" stroke="#dc2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ error }}
        </div>
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <button 
          type="button" 
          class="btn-cancel" 
          @click="$emit('cancel')"
          :disabled="loading"
        >
          Annuler
        </button>
        <button 
          type="button" 
          class="btn-delete" 
          @click="handleConfirm"
          :disabled="loading"
        >
          <div v-if="loading" class="loading-spinner"></div>
          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ loading ? 'Suppression...' : 'Supprimer définitivement' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeleteConfirmationModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Confirmer la suppression'
    },
    message: {
      type: String,
      default: 'Êtes-vous sûr de vouloir effectuer cette suppression ?'
    },
    entityDetails: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showShake: false
    }
  },
  methods: {
    handleConfirm() {
      if (this.loading) return
      this.$emit('confirm')
    },
    shake() {
      this.showShake = true
      setTimeout(() => {
        this.showShake = false
      }, 600)
    }
  },
  emits: ['cancel', 'confirm']
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
}

.delete-modal {
  background: #ffffff;
  border-radius: 16px;
  padding: 0;
  min-width: 420px;
  max-width: 500px;
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid #f3f4f6;
  overflow: hidden;
  animation: modalSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center;
}

.modal-shake {
  animation: shake 0.6s ease-in-out;
}

.modal-header {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  padding: 24px;
  text-align: center;
  border-bottom: 1px solid #fecaca;
}

.danger-icon {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.modal-header h3 {
  margin: 0;
  color: #dc2626;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.modal-content {
  padding: 24px;
}

.main-message {
  color: #374151;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0 0 20px 0;
  text-align: center;
}

.entity-details {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-weight: 500;
  color: #6b7280;
  font-size: 0.9rem;
}

.detail-value {
  font-weight: 600;
  color: #111827;
  font-size: 0.9rem;
  max-width: 60%;
  text-align: right;
  word-break: break-word;
}

.warning-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fffbeb;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 12px 16px;
  color: #d97706;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 20px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  color: #dc2626;
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 16px;
  animation: slideDown 0.3s ease-out;
}

.modal-actions {
  display: flex;
  gap: 12px;
  padding: 20px 24px 24px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.btn-cancel {
  flex: 1;
  padding: 12px 24px;
  border: 2px solid #d1d5db;
  background: #ffffff;
  color: #6b7280;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover:not(:disabled) {
  border-color: #9ca3af;
  color: #374151;
  background: #f9fafb;
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-delete {
  flex: 1;
  padding: 12px 24px;
  border: none;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2);
}

.btn-delete:hover:not(:disabled) {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
}

.btn-delete:active {
  transform: translateY(0);
}

.btn-delete:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
  20%, 40%, 60%, 80% { transform: translateX(8px); }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 480px) {
  .delete-modal {
    min-width: 320px;
    margin: 20px;
  }
  
  .modal-header {
    padding: 20px;
  }
  
  .modal-content {
    padding: 20px;
  }
  
  .modal-actions {
    flex-direction: column;
    gap: 8px;
  }
}
</style>